import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import api from '../lib/api';
import {
  ShieldCheck,
  Eye,
  Pencil,
  Search,
  Clock,
  User,
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  LogIn,
  ShieldAlert,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

const ACTION_CONFIG = {
  view: { label: 'Eingesehen', icon: Eye, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  edit: { label: 'Geändert', icon: Pencil, color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  delete: { label: 'Gelöscht', icon: Trash2, color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  restore: { label: 'Wiederhergestellt', icon: RotateCcw, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
  download: { label: 'Heruntergeladen', icon: FileText, color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  login: { label: 'Login', icon: LogIn, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
  login_failed: { label: 'Login fehlgeschl.', icon: ShieldAlert, color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
};

// Welche resource_type sind soft-delete-restorable?
const RESTORABLE_TYPES = new Set(['employee', 'room', 'location']);
const RESTORE_ENDPOINTS = {
  employee: '/employees',
  room: '/rooms',
  location: '/locations',
};

const PAGE_SIZE = 50;

export default function AuditLog({ embedded = false }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [page, setPage] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 500 };
      if (filterAction !== 'all') params.action = filterAction;
      const res = await api.get('/audit', { params });
      setEntries(res.data || []);
    } catch (error) {
      console.error('Failed to load audit log:', error);
      toast.error('Fehler beim Laden des Audit-Logs');
    } finally {
      setLoading(false);
    }
  }, [filterAction]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRestore = async (entry) => {
    const endpoint = RESTORE_ENDPOINTS[entry.resource_type];
    if (!endpoint) return;
    try {
      await api.post(`${endpoint}/${entry.resource_id}/restore`);
      toast.success('Wiederherstellung erfolgreich');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wiederherstellung fehlgeschlagen');
    }
  };

  // Prüft, ob ein Lösch-Eintrag noch wiederherstellbar ist (≤30 Tage alt
  // und noch kein nachfolgender Restore-Eintrag für dieselbe resource_id).
  const isRestorable = useCallback((entry) => {
    if (entry.action !== 'delete') return false;
    if (!RESTORABLE_TYPES.has(entry.resource_type)) return false;
    try {
      const days = differenceInDays(new Date(), parseISO(entry.timestamp));
      if (days > 30) return false;
    } catch {
      return false;
    }
    // Späterer Restore vorhanden? → nicht mehr aus dieser Zeile heraus restoreable
    const laterRestore = entries.find(
      (e) =>
        e.action === 'restore' &&
        e.resource_id === entry.resource_id &&
        e.resource_type === entry.resource_type &&
        new Date(e.timestamp) > new Date(entry.timestamp),
    );
    return !laterRestore;
  }, [entries]);

  // Filter entries by search
  const filteredEntries = entries.filter(e => {
    if (!filterSearch) return true;
    const search = filterSearch.toLowerCase();
    return (
      (e.user_name || '').toLowerCase().includes(search) ||
      (e.user_email || '').toLowerCase().includes(search) ||
      (e.details || '').toLowerCase().includes(search) ||
      (e.resource_id || '').toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredEntries.length / PAGE_SIZE);
  const paginatedEntries = filteredEntries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Stats
  const viewCount = entries.filter(e => e.action === 'view').length;
  const editCount = entries.filter(e => e.action === 'edit').length;
  const loginCount = entries.filter(e => e.action === 'login').length;
  const loginFailedCount = entries.filter(e => e.action === 'login_failed').length;
  const uniqueUsers = new Set(entries.map(e => e.user_email)).size;

  const formatTimestamp = (ts) => {
    try {
      return format(parseISO(ts), 'dd.MM.yyyy HH:mm:ss', { locale: de });
    } catch {
      return ts;
    }
  };

  const formatTimeAgo = (ts) => {
    try {
      const date = parseISO(ts);
      const now = new Date();
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      const diffH = Math.floor(diffMin / 60);
      const diffD = Math.floor(diffH / 24);
      if (diffMin < 1) return 'Gerade eben';
      if (diffMin < 60) return `vor ${diffMin} Min.`;
      if (diffH < 24) return `vor ${diffH} Std.`;
      if (diffD < 7) return `vor ${diffD} Tag${diffD > 1 ? 'en' : ''}`;
      return format(date, 'dd.MM.yyyy', { locale: de });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
          <Skeleton className="h-[400px] rounded-xl" />
        </div>
      </MaybeLayout>
    );
  }

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Zugriffsprotokolle</h1>
            <p className="text-muted-foreground mt-1">
              {entries.length} Einträge insgesamt
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} className="rounded-full" data-testid="refresh-audit-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Eye className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="stat-views">{viewCount}</p>
                <p className="text-xs text-muted-foreground">Einsichtnahmen</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Pencil className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="stat-edits">{editCount}</p>
                <p className="text-xs text-muted-foreground">Änderungen</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <LogIn className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="stat-logins">{loginCount}</p>
                <p className="text-xs text-muted-foreground">
                  Logins
                  {loginFailedCount > 0 && (
                    <span className="text-red-500 ml-1">({loginFailedCount} fehlgeschl.)</span>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-green-100 dark:bg-green-900/30">
                <User className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="stat-users">{uniqueUsers}</p>
                <p className="text-xs text-muted-foreground">Aktive Benutzer</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick-Filter-Chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1">Schnellfilter:</span>
          {[
            { value: 'all', label: 'Alle' },
            { value: 'delete', label: 'Nur Löschungen' },
            { value: 'restore', label: 'Wiederherstellungen' },
            { value: 'login_failed', label: 'Login-Fehlversuche' },
          ].map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => { setFilterAction(chip.value); setPage(0); }}
              className={`px-3 py-1 rounded-full text-xs border transition ${
                filterAction === chip.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background hover:bg-muted border-border'
              }`}
              data-testid={`audit-quickfilter-${chip.value}`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suche nach Name, E-Mail, Details..."
                  value={filterSearch}
                  onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
                  className="pl-9"
                  data-testid="audit-search"
                />
              </div>
              <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(0); }}>
                <SelectTrigger className="w-[180px]" data-testid="audit-filter-action">
                  <SelectValue placeholder="Alle Aktionen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Aktionen</SelectItem>
                  <SelectItem value="view">Eingesehen</SelectItem>
                  <SelectItem value="edit">Geändert</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="login_failed">Login fehlgeschlagen</SelectItem>
                  <SelectItem value="delete">Gelöscht</SelectItem>
                  <SelectItem value="download">Heruntergeladen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-border/60">
          <CardContent className="p-0">
            {filteredEntries.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Zeitpunkt</TableHead>
                      <TableHead className="w-[160px]">Benutzer</TableHead>
                      <TableHead className="w-[120px]">Aktion</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead className="w-[140px] text-right">Optionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedEntries.map((entry) => {
                      const actionCfg = ACTION_CONFIG[entry.action] || ACTION_CONFIG.view;
                      const ActionIcon = actionCfg.icon;
                      const restorable = isRestorable(entry);

                      return (
                        <TableRow key={entry.id} data-testid={`audit-row-${entry.id}`}>
                          <TableCell className="py-3">
                            <div className="flex items-center gap-2">
                              <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium">{formatTimeAgo(entry.timestamp)}</p>
                                <p className="text-[11px] text-muted-foreground">{formatTimestamp(entry.timestamp)}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <div>
                              <p className="text-sm font-medium">{entry.user_name}</p>
                              <p className="text-[11px] text-muted-foreground">{entry.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge variant="outline" className={`text-xs gap-1 ${actionCfg.color}`}>
                              <ActionIcon className="h-3 w-3" />
                              {actionCfg.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3">
                            <p className="text-sm">{entry.details}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {entry.resource_type} / {entry.resource_id?.substring(0, 8)}...
                            </p>
                          </TableCell>
                          <TableCell className="py-3 text-right">
                            {restorable && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full"
                                onClick={() => handleRestore(entry)}
                                data-testid={`restore-${entry.resource_type}-${entry.resource_id}`}
                              >
                                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                Wiederherstellen
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
                    <p className="text-sm text-muted-foreground">
                      Seite {page + 1} von {totalPages} ({filteredEntries.length} Einträge)
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                        data-testid="audit-prev-page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage(p => p + 1)}
                        data-testid="audit-next-page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 text-muted-foreground">
                <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">Keine Einträge</p>
                <p className="text-sm mt-1">
                  {filterSearch || filterAction !== 'all'
                    ? 'Keine Einträge für die gewählten Filter gefunden.'
                    : 'Noch keine Zugriffsprotokolle vorhanden.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MaybeLayout>
  );
}
